export type Theme = 'dark' | 'light';

const KEY = 'wf_theme';

function read(): string | null {
  try {
    return localStorage.getItem(KEY);
  } catch {
    return null; // storage disabled (private mode) — treat as "no stored choice"
  }
}

function write(value: Theme): void {
  try {
    localStorage.setItem(KEY, value);
  } catch {
    // storage unavailable: the <html> stamp below still themes this session
  }
}

/** The user's persisted choice, or null when they have never toggled (OS default in effect). */
export function storedTheme(): Theme | null {
  const v = read();
  return v === 'dark' || v === 'light' ? v : null;
}

function prefersDark(): boolean {
  return typeof matchMedia === 'function' && matchMedia('(prefers-color-scheme: dark)').matches;
}

/** What is on screen right now: the stored choice, else the OS preference. */
export function effectiveTheme(): Theme {
  return storedTheme() ?? (prefersDark() ? 'dark' : 'light');
}

/** Flip, persist, and stamp `<html data-theme>`. Returns the new theme. */
export function toggleTheme(): Theme {
  const next: Theme = effectiveTheme() === 'dark' ? 'light' : 'dark';
  write(next);
  document.documentElement.dataset.theme = next;
  return next;
}
